import { useEffect, useRef, useCallback, useState } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import injectSheet, { useTheme } from 'react-jss'
import classNames from 'classnames'
import gsap from 'gsap'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import BtnLink from '@/components/BtnLink'
import WpContactForm7 from '@/components/WpContactForm7'
import Carousel from '@/components/Carousel'
import YoutubeVideo from '@/components/YoutubeVideo'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import { wrapSpecificWorldWithSpan } from '@/utils/strings'
import actions from '@/actions'
import constants from '@/constants'
import * as formActions from '@/actions/form'
import style from './style'

const libraries = ['places'] // To keep out of component because of performance

gsap.registerPlugin(ScrollToPlugin)

const ModalStore = ({
  classes,
  currentID,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: constants.config.GOOGLE_MAP_API_KEY,
    libraries, // Mandatory library "places" to work with geocoding
  })
  const $root = useRef()
  const $modal = useRef()
  const $wrapper = useRef()
  const theme = useTheme()
  const [ready, setReady] = useState(false)
  const formId = useRef()

  const { currentStore, isModalOpen, strings, currentForm } = useSelector((state) => ({
    currentStore: state.stores.results.find((s) => s.id.toString() === currentID.toString()),
    isModalOpen: state.modal.open,
    mediaQueryKey: state.mediaQuery.key,
    pathname: state.router.location.pathname,
    strings: state.language.string,
    currentForm: state.form[formId.current] || [],
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setModalOpen = useCallback((bool) => dispatch(actions.modal.setOpen(bool)), [dispatch])
  const fetchForm = useCallback((id) => dispatch(formActions.fetchForm(id)), [dispatch])
  const sendContactForm = useCallback((id, data, isMultipart) => dispatch(formActions.sendContactForm(id, data, isMultipart)), [dispatch])

  /*------------------------------
  If Strings exist call API form
  ------------------------------*/
  useEffect(() => {
    if (!ready) setReady(true)
    if (strings.appointment_form_id) {
      formId.current = strings.appointment_form_id
      fetchForm(strings.appointment_form_id)
    }
  }, [strings])

  useOnClickOutside($modal, () => {
    if (isModalOpen) setModalOpen(false)
  })

  /*------------------------------
  Initialize
  ------------------------------*/
  const init = () => {
    gsap.set($modal.current, { x: '100%', autoAlpha: 0 })
    gsap.set($wrapper.current, { x: '100%', autoAlpha: 0 })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && e.keyCode === 27 && isModalOpen) setModalOpen(false)
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isModalOpen])

  const openModal = () => {
    gsap.killTweensOf([$modal.current, $wrapper.current])
    const tl = gsap.timeline({ ease: 'power3.out' })
    tl
      .to($modal.current, {
        onStart: () => $wrapper.current.scrollTo(0, 0),
        x: '0%',
        autoAlpha: 1,
        ease: 'power3.inOut',
      })
      .to($wrapper.current, {
        onStart: () => $wrapper.current.scrollTo(0, 0),
        x: '0%',
        autoAlpha: 1,
        ease: 'power3.inOut',
      }, '<0.2')
  }

  const closeModal = () => {
    if ($wrapper.current) {
      gsap.killTweensOf([$modal.current, $wrapper.current])
      const tl = gsap.timeline({ ease: 'power3.out' })
      tl
        .to([$modal.current, $wrapper.current], {
          x: '100%',
          autoAlpha: 0,
          ease: 'power3.inOut',
        })
    }
  }

  useEffect(() => {
    if (isModalOpen) openModal()
    if (!isModalOpen) closeModal()
  }, [isModalOpen])

  /*------------------------------
  Init
  ------------------------------*/
  useEffect(() => {
    if (ready && !isModalOpen) init()
  }, [ready])

  const handleClose = () => setModalOpen(false)

  const goToAppointmentForm = () => gsap.to($wrapper.current, { duration: 1, scrollTo: '#appointment' })

  /*------------------------------
  Render Info Section
  ------------------------------*/
  const renderInfoSection = () => {
    return (
      <>
        <div className={classes.left}>
          {/* {currentStore.category && <div className={classes.category}>{currentStore.category[0]?.name}</div>} */}
          {currentStore.title && (
            <div className={classes.info}>
              <div className={classes.icon}>
                <svg className={currentStore.category[0]?.term_id === 6 ? 'center' : 'green'}><use xlinkHref={`#ico-${currentStore.category[0]?.term_id === 6 ? 'center' : 'green'}`} /></svg>
              </div>
              <div
                className={classes.title}
                dangerouslySetInnerHTML={{ __html: currentStore.title }}
              />
            </div>
          )}
          {currentStore.address && (
            <div
              className={classes.address}
              dangerouslySetInnerHTML={{ __html: currentStore.address }}
            />
          )}
          <BtnLink
            link={`http://www.google.com/maps/place/${currentStore.lat},${currentStore.lng}`}
            label={strings.roads_label}
            theme="orange"
            slim
          />
        </div>
        <div className={`${classes.right} ${classes.contact}`}>
          <div className={classes.iconsList}>
            {currentStore.tel && (
              <div className={classes.itemList}>
                <a href={`tel:${currentStore.tel}`}>
                  <svg><use xlinkHref="#ico-phone" /></svg>
                  <span>{currentStore.tel}</span>
                </a>
              </div>
            )}
            {currentStore.website && (
              <div className={classes.itemList}>
                <a href={currentStore.website} target="_blank">
                  <svg><use xlinkHref="#ico-sedi" /></svg>
                  <span>{currentStore.website}</span>
                </a>
              </div>
            )}
          </div>
          <BtnLink
            onClick={goToAppointmentForm}
            label={strings.appointment_label}
            theme="orange"
            fill
          />
        </div>
      </>
    )
  }

  /*------------------------------
  Render Desc
  ------------------------------*/
  const renderDesc = () => {
    return (
      <>
        {currentStore.description && (
          <div className={`${classes.left} ${classes.secondLine}`}>
            <div className={classes.pretitle}>{strings.description_label}</div>
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: currentStore.description }}
            />
          </div>
        )}
        {currentStore.hours && (
          <div className={`${classes.right} ${classes.secondLine}`}>
            <div className={classes.pretitle}>{strings.hours_label}</div>
            <div
              className={classes.hours}
              dangerouslySetInnerHTML={{ __html: wrapSpecificWorldWithSpan(currentStore.hours) }}
            />
          </div>
        )}
      </>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGallery = () => {
    const gallery = currentStore.gallery.map((g) => ({
      src: g.full[0],
      width: g.full[1],
      height: g.full[2],
    }))
    return currentStore.gallery.[0].full && (
      <div className={`${classes.full} ${classes.thirdLine}`}>
        <div className={classes.pretitle}>{strings.gallery_label}</div>
        <Carousel
          className={classes.gallery}
          imagesList={gallery}
          parallax
          dots
          arrows
        />
      </div>
    )
  }

  /*------------------------------
  Render Video
  ------------------------------*/
  const renderVideo = () => {
    return currentStore.video && (
      <div className={`${classes.full} ${classes.thirdLine}`}>
        <div className={classes.pretitle}>{strings.video_label}</div>
        <YoutubeVideo
          className={classes.video}
          videoId={currentStore.video.id}
          poster={currentStore.video.poster || null}
        />
      </div>
    )
  }

  /*------------------------------
  Render MAP
  ------------------------------*/
  const renderMap = () => {
    return isLoaded && (
      <div className={`${classes.full} ${classes.thirdLine}`}>
        {currentStore.lat && (
          <>
            <div className={classes.pretitle}>{strings.where_label}</div>
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '400px',
              }}
              center={{
                lat: Number(currentStore.lat),
                lng: Number(currentStore.lng),
              }}
              zoom={10}
              options={{
                disableDefaultUI: true,
                scrollwheel: false,
              }}
            >
              <Marker
                position={{
                  lat: Number(currentStore.lat),
                  lng: Number(currentStore.lng),
                }}
                icon={{
                  path: 'M7.72 0h25C39.59 0 42.4 5.62 39 12.5l-12.5 25c-3.5 6.88-9.1 6.88-12.5 0l-12.5-25C-2 5.62.84 0 7.72 0z',
                  fillColor: theme.markerColor[currentStore.category[0].slug],
                  fillOpacity: 1,
                  scale: 0.8,
                  strokeColor: 'rgba(0, 0, 0, 0.2)',
                  strokeWeight: 3,
                }}
              />
            </GoogleMap>
          </>
        )}
      </div>
    )
  }

  const renderForm = () => {
    return (
      <div
        className={`${classes.full} ${classes.thirdLine}`}
        id="appointment"
      >
        <div className={classes.pretitle}>{strings.appointment_label}</div>
        <WpContactForm7
          fields={currentForm}
          formID={strings.appointment_form_id}
          submitLabel={strings.send_label}
          sendContactForm={sendContactForm}
          hiddenValues={{
            centro: currentStore.title,
          }}
        />
      </div>
    )
  }

  return ready && (
    <div
      className={classNames({
        [classes.root]: true,
        [classes.visible]: isModalOpen,
      })}
      ref={$root}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={classes.modal}
        ref={$modal}
      >
        <button
          className={classNames({
            [classes.close]: true,
            [classes.visible]: isModalOpen,
          })}
          onClick={handleClose}
          aria-label="close-modal"
        >
          <svg><use xlinkHref="#ico-close" /></svg>
        </button>
        <div className={classes.container}>
          <div
            ref={$wrapper}
            className={classes.wrapper}
          >
            <div className={classes.scrollable}>
              {currentStore && (
                <>
                  {renderInfoSection()}
                  {renderDesc()}
                  {renderGallery()}
                  {renderVideo()}
                  {renderMap()}
                  {renderForm()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default injectSheet(style)(ModalStore)
